import { useMemo, useRef } from 'react'
import { UIMessage } from 'ai'
import { getMessageTimestamp, getToolCalls } from '../utils/message-utils';

export interface ToolCall {
  id: string
  toolName: string
  timestamp: number
  messageId: string
}

export interface ToolGroup {
  timestamp: number
  tools: ToolCall[]
  isNew?: boolean
}

function extractToolCalls(messages: UIMessage[], isLoading = false) {
  const filteredMessages = isLoading 
    ? messages.filter((_, index) => index !== messages.length - 1)
    : messages;
    
  const toolCalls = filteredMessages
    .flatMap(message => {
      const timestamp = getMessageTimestamp(message);
      return getToolCalls(message).map(call => ({
        id: call.callId || `tool-${Date.now()}`,
        toolName: call.toolName || 'unknown',
        messageId: message.id,
        timestamp
      }));
    })
    .filter(tool => tool.toolName && tool.toolName !== 'unknown');

  const groupedByTimestamp = toolCalls.reduce((acc, tool) => {
    const roundedTime = new Date(tool.timestamp);
    roundedTime.setSeconds(0, 0);
    const timeKey = roundedTime.getTime();
    
    if (!acc[timeKey]) {
      acc[timeKey] = {
        timestamp: timeKey,
        tools: []
      };
    }
    
    acc[timeKey].tools.push(tool);
    return acc;
  }, {} as Record<number, ToolGroup>);

  return Object.values(groupedByTimestamp)
    .sort((a, b) => b.timestamp - a.timestamp);
}

export function ReasoningToolsProvider({ 
  messages, 
  isLoading = false 
}: { 
  messages: UIMessage[], 
  isLoading?: boolean 
}) {
  const seenGroupsRef = useRef(new Set<number>());
  
  const toolsFingerprint = useMemo(() => {
    const messagesToProcess = isLoading 
      ? messages.filter((_, index) => index !== messages.length - 1)
      : messages;
    
    return messagesToProcess
      .flatMap(message => getToolCalls(message))
      .map(call => `${call.toolName}:${call.callId}`)
      .join('|');
  }, [messages, isLoading])
  
  const toolGroups = useMemo(() => {
    const newGroups = extractToolCalls(messages, isLoading);
    
    const result = newGroups.map(group => {
      const isNew = !seenGroupsRef.current.has(group.timestamp);
      seenGroupsRef.current.add(group.timestamp);
      return {
        ...group,
        isNew
      };
    });
    
    return result;
  }, [toolsFingerprint, isLoading])

  return { toolGroups }
}

export function useReasoningTools(messages: UIMessage[], isLoading?: boolean) {
  return ReasoningToolsProvider({ messages, isLoading }).toolGroups
} 
